/**
 * Buy Controls — Compact / Expand-on-Tap behaviour
 *
 * When a `.buy-controls` container is narrower than 360 px (detected via
 * ResizeObserver), the quantity selector is hidden by CSS container query and
 * the ATC button fills the row.  On tap the row expands into a unified
 * [−][qty][+][Confirm ✓] bar.  Tapping Confirm submits; tapping outside
 * collapses (keeping the chosen quantity).
 *
 * IMPORTANT: The theme's component framework registers its click delegate on
 * `document` in the CAPTURE phase.  To intercept before it, we register on
 * `window` (which fires before `document` during capture).
 */
(function buyControlsCompact() {
  'use strict';

  var COMPACT_THRESHOLD = 360;
  var CONFIRM_TEXT = 'Confirm';

  /**
   * @param {Element} el
   */
  function initBuyControls(el) {
    var atcButton = el.querySelector('.add-to-cart-button');
    if (!atcButton) return;

    var textSpan = atcButton.querySelector('.add-to-cart-text__content span > span');

    // Store state on the element so the window-level handler can access it
    el.__bcState = {
      compact: false,
      expanded: false,
      originalText: textSpan ? textSpan.textContent : '',
      textSpan: textSpan,
      atcButton: atcButton,
      _outsideHandler: null,
    };

    // ── Compact detection via ResizeObserver ──

    // Set initial state synchronously (ResizeObserver fires async)
    el.__bcState.compact = el.offsetWidth < COMPACT_THRESHOLD;

    var ro = new ResizeObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        var width = entries[i].contentBoxSize
          ? entries[i].contentBoxSize[0].inlineSize
          : entries[i].contentRect.width;

        var wasCompact = el.__bcState.compact;
        el.__bcState.compact = width < COMPACT_THRESHOLD;

        // Leaving compact mode → collapse
        if (wasCompact && !el.__bcState.compact && el.__bcState.expanded) {
          collapse(el);
        }
      }
    });
    ro.observe(el);

    // ── Cart update → collapse after successful add ──

    el.addEventListener('cart:update', function (e) {
      if (!el.__bcState.expanded) return;
      if (e.detail && e.detail.data && e.detail.data.didError) return;
      collapse(el);
    });

    // ── Variant change → collapse + reset qty ──

    var productForm = el.closest('product-form-component');
    if (productForm) {
      productForm.addEventListener('variant:update', function () {
        if (el.__bcState.expanded) {
          collapse(el);
        }
        var qtyInput = el.querySelector('.quantity-selector input[type="number"]');
        if (qtyInput) {
          qtyInput.value = qtyInput.min || '1';
        }
      });
    }
  }

  // ── Window-level click interception (capture phase, fires before document) ──

  window.addEventListener(
    'click',
    function (e) {
      var target = e.target;
      if (!(target instanceof Element)) return;

      var atcBtn = target.closest('.add-to-cart-button');
      if (!atcBtn) return;

      var buyControls = atcBtn.closest('.buy-controls');
      if (!buyControls || !buyControls.__bcState) return;

      var state = buyControls.__bcState;
      if (!state.compact) return; // wide mode — normal flow
      if (atcBtn.disabled) return; // sold out

      if (!state.expanded) {
        // First tap → intercept before the framework sees it
        e.preventDefault();
        e.stopPropagation();
        expand(buyControls);
        return;
      }

      // Already expanded → let the normal submit flow through
    },
    { capture: true }
  );

  // ── Expand ──

  function expand(el) {
    var state = el.__bcState;
    state.expanded = true;
    el.classList.add('buy-controls--expanded');

    // Swap button text
    if (state.textSpan) {
      state.textSpan.textContent = CONFIRM_TEXT;
    }

    // Focus confirm button
    state.atcButton.focus();

    // Click-outside listener (next tick to avoid self-trigger)
    requestAnimationFrame(function () {
      document.addEventListener(
        'click',
        (state._outsideHandler = function (e) {
          if (!el.contains(e.target)) {
            collapse(el);
          }
        })
      );
    });
  }

  // ── Collapse ──

  function collapse(el) {
    var state = el.__bcState;
    if (!state.expanded) return;
    state.expanded = false;
    el.classList.remove('buy-controls--expanded');

    // Restore button text
    if (state.textSpan && state.originalText) {
      state.textSpan.textContent = state.originalText;
    }

    // Remove click-outside listener
    if (state._outsideHandler) {
      document.removeEventListener('click', state._outsideHandler);
      state._outsideHandler = null;
    }
  }

  // ── Initialise ──

  function init() {
    var elements = document.querySelectorAll('.buy-controls');
    for (var i = 0; i < elements.length; i++) {
      initBuyControls(elements[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
